import { getSceneState } from "../sceneStore";
import {
  areHistorySnapshotsEqual,
  getHistoryActions,
  makeHistorySnapshot,
  type HistorySnapshot,
} from "../historyStore";
import { shouldBlockSceneAuthoringMutation } from "../../utils/physicsGuards";
import { getProjectSaveActions } from "../projectSaveStore";

// Depth counter to prevent nested history saves during a parent action
let historyWrapDepth = 0;

type WithHistoryOptions = {
  actionName?: string;
  allowDuringPhysics?: boolean;
};

export const withHistory = <T extends unknown[], R>(
  action: (...args: T) => R,
  options: WithHistoryOptions = {}
) => {
  return (...args: T): R => {
    const actionName = options.actionName || action.name || "scene mutation";
    if (!options.allowDuringPhysics && shouldBlockSceneAuthoringMutation(actionName)) {
      return undefined as R;
    }

    const isTopLevel = historyWrapDepth === 0;
    let before: HistorySnapshot | null = null;

    if (isTopLevel) {
      before = makeHistorySnapshot(getSceneState());
    }

    historyWrapDepth++;
    try {
      const result = action(...args);

      if (isTopLevel && before) {
        const after = makeHistorySnapshot(getSceneState());
        if (!areHistorySnapshotsEqual(before, after)) {
          const historyActions = getHistoryActions();
          // Save the BEFORE snapshot so undo restores the state prior to the action
          historyActions.save(before);
          getProjectSaveActions().markProjectDirty();
        }
      }

      return result;
    } finally {
      historyWrapDepth--;
    }
  };
};
